import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useText, useImage } from "../context/ContentContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776935359455-94263068537c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBkZXNpZ258ZW58MHx8fHwxNzc4MDYyMTQ3fDA&ixlib=rb-4.1.0&q=85";
const SCULPTURE_IMG =
  "https://images.unsplash.com/photo-1701884314987-09fb749e023b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwc2N1bHB0dXJlJTIwbHV4dXJ5fGVufDB8fHx8MTc3ODA2MjE0N3ww&ixlib=rb-4.1.0&q=85";
const SOFA_IMG =
  "https://images.unsplash.com/photo-1777604602765-1d5114114891?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBkZXNpZ258ZW58MHx8fHwxNzc4MDYyMTQ3fDA&ixlib=rb-4.1.0&q=85";
const CARVED_IMG =
  "https://images.unsplash.com/photo-1706980954877-7b4be6101a55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwc2N1bHB0dXJlJTIwbHV4dXJ5fGVufDB8fHx8MTc3ODA2MjE0N3ww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useTranslation();
  const heroOverline = useText("home_hero_overline", "home.overline");
  const heroLine1 = useText("home_hero_line1", "home.title_1");
  const heroLine2 = useText("home_hero_line2", "home.title_2");
  const heroLine3 = useText("home_hero_line3", "home.title_3");
  const heroLine4 = useText("home_hero_line4", "home.title_4");
  const heroLede = useText("home_hero_lede", "home.lede");
  const storyP1 = useText("home_story_p1", "home.story_p1");
  const storyP2 = useText("home_story_p2", "home.story_p2");
  const heroImg = useImage("home_hero_image", HERO_IMG);
  const storyImg = useImage("home_story_image", SCULPTURE_IMG);
  const bentoFurniture = useImage("home_bento_furniture", SOFA_IMG);
  const bentoJewelry = useImage("home_bento_jewelry", SCULPTURE_IMG);
  const bentoCrafts = useImage("home_bento_crafts", CARVED_IMG);
  const bentoDecoration = useImage("home_bento_decoration", HERO_IMG);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 6)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden">
        <img src={heroImg} alt="Maison Afra" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 grain"></div>

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 pt-40 md:pt-56 pb-32">
          <div className="max-w-3xl">
            <div className="overline mb-8 fade-in">{heroOverline}</div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-[#F5F5F0] fade-up">
              {heroLine1} <br />
              <em className="text-[#D4AF37] not-italic">{heroLine2}</em>, <br />
              {heroLine3} <br />
              {heroLine4}
            </h1>
            <p className="mt-10 text-[#A3A39D] max-w-xl text-base md:text-lg leading-relaxed fade-up">
              {heroLede}
            </p>
            <div className="mt-12 flex flex-wrap gap-4 fade-up">
              <Link to="/boutique" data-testid="hero-cta-shop" className="btn-primary inline-flex items-center gap-2">
                {t("home.cta_shop")}
                <ArrowUpRight strokeWidth={1.25} size={16} />
              </Link>
              <Link to="/maison" data-testid="hero-cta-story" className="btn-ghost">
                {t("home.cta_story")}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-6 md:left-12 right-6 md:right-12 flex justify-between items-end z-10">
          <div className="overline text-[#A3A39D]">{t("home.permanence")}</div>
          <div className="overline text-[#A3A39D] hidden md:block">Paris · Abidjan</div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/10 bg-[#0a0a0b] py-6">
        <Marquee speed={30} gradient={false}>
          <div className="flex items-center gap-16 text-xs tracking-[0.3em] uppercase text-[#D4AF37]/60 font-light pr-16">
            <span>{t("home.marquee")}</span>
            <span className="text-[#D4AF37]">✦</span>
            <span>{t("home.marquee")}</span>
            <span className="text-[#D4AF37]">✦</span>
          </div>
        </Marquee>
      </section>

      {/* BENTO */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="overline mb-5">{t("home.universes_overline")}</div>
            <h2 className="font-serif text-4xl md:text-6xl text-[#F5F5F0]">
              {t("home.universes_title_1")} <br />
              <em className="text-[#D4AF37] not-italic">{t("home.universes_title_2")}</em>
            </h2>
          </div>
          <p className="max-w-md text-[#A3A39D] leading-relaxed">{t("home.universes_text")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <BentoCategory to="/categorie/meubles" img={bentoFurniture} overline="01 — Mobilier" title={t("home.u_furniture")} className="md:col-span-7 aspect-[4/5] md:aspect-[7/6]" />
          <BentoCategory to="/categorie/bijoux" img={bentoJewelry} overline="02 — Bijoux" title={t("home.u_jewelry")} className="md:col-span-5 aspect-[4/5] md:aspect-[5/6]" />
          <BentoCategory to="/categorie/artisanat" img={bentoCrafts} overline="03 — Artisanat" title={t("home.u_crafts")} className="md:col-span-5 aspect-[5/4] md:aspect-[5/5]" />
          <BentoCategory to="/categorie/decoration" img={bentoDecoration} overline="04 — Décoration" title={t("home.u_decoration")} className="md:col-span-7 aspect-[5/4] md:aspect-[7/5]" />
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-24 md:py-32 border-t border-white/10">
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="overline mb-5">{t("home.signature_overline")}</div>
            <h2 className="font-serif text-4xl md:text-6xl text-[#F5F5F0]">
              {t("home.signature_title_1")} <em className="text-[#D4AF37] not-italic">{t("home.signature_title_2")}</em>
            </h2>
          </div>
          <Link to="/boutique" data-testid="see-all-link" className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] hover:text-[#F0C050] inline-flex items-center gap-2">
            {t("home.see_all")} <ArrowUpRight size={14} strokeWidth={1.25} />
          </Link>
        </div>

        {featured.length === 0 ? (
          <div className="text-[#A3A39D] text-sm">{t("catalog.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
            {featured.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
          </div>
        )}
      </section>

      {/* STORY */}
      <section className="bg-[#151516] border-y border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-24 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline mb-5">{t("home.story_overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] mb-8 leading-tight">
              {t("home.story_title_1")} <br />
              <em className="text-[#D4AF37] not-italic">{t("home.story_title_2")}</em>.
            </h2>
            <p className="text-[#A3A39D] leading-relaxed mb-5">{storyP1}</p>
            <p className="text-[#A3A39D] leading-relaxed mb-10">{storyP2}</p>
            <Link to="/maison" data-testid="story-cta" className="btn-ghost">{t("home.story_cta")}</Link>
          </div>
          <div className="img-hover aspect-[4/5] bg-[#0a0a0b]">
            <img src={storyImg} alt="Artisanat africain" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}

function BentoCategory({ to, img, overline, title, className = "" }) {
  return (
    <Link to={to} data-testid={`bento-${overline.replace(/\s/g, "")}`} className={`relative img-hover bg-[#151516] ${className}`}>
      <img src={img} alt={title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
      <div className="absolute left-6 right-6 bottom-6 md:left-8 md:bottom-8 flex justify-between items-end gap-4">
        <div>
          <div className="overline mb-2 text-[10px]">{overline}</div>
          <div className="font-serif text-2xl md:text-3xl text-[#F5F5F0] leading-tight">{title}</div>
        </div>
        <ArrowUpRight strokeWidth={1.25} size={22} className="text-[#D4AF37]" />
      </div>
    </Link>
  );
}
